/**
 * Controlled vehicle feature taxonomy. Single source of truth consumed by:
 *  - manual editor checkbox grid (apps/web/app/admin/inventory/new/step-4)
 *  - SRP features filter (apps/web/components/public/srp/filter-panel.tsx)
 *  - Lead detail view (humanizes feature codes)
 *  - (future) feed preset valueMappings.features validation
 *
 * Vehicle.features text[] enforces taxonomy values via Zod parse on insert
 * /update; non-taxonomy strings rejected. Freeform notes go to Vehicle.customFeatures.
 */
export const FEATURE_TAXONOMY = {
  "Comfort & Convenience": [
    "Sunroof",
    "Panoramic Sunroof",
    "Leather Seats",
    "Heated Seats",
    "Cooled Seats",
    "Heated Steering Wheel",
    "Power Driver Seat",
    "Memory Seats",
    "Keyless Entry",
    "Push-Button Start",
    "Remote Start",
    "Power Liftgate",
    "Hands-Free Liftgate",
    "Third Row Seating",
  ],
  "Safety & Driver Assist": [
    "Backup Camera",
    "360° Camera",
    "Blind Spot Monitor",
    "Lane Departure Warning",
    "Lane Keep Assist",
    "Adaptive Cruise Control",
    "Forward Collision Warning",
    "Automatic Emergency Braking",
    "Parking Sensors",
    "Cross Traffic Alert",
  ],
  Technology: [
    "Bluetooth",
    "Apple CarPlay",
    "Android Auto",
    "Navigation",
    "Premium Audio",
    "Wireless Phone Charging",
    "Heads-Up Display",
    "WiFi Hotspot",
  ],
  Performance: ["Tow Package", "Sport Package", "Performance Package"],
  Exterior: ["Alloy Wheels", "Chrome Wheels", "Tinted Windows", "Roof Rack", "Running Boards"],
} as const;

export type FeatureCategory = keyof typeof FEATURE_TAXONOMY;

export const ALL_FEATURES: readonly string[] = Object.values(FEATURE_TAXONOMY).flat();

const ALL_FEATURES_SET = new Set<string>(ALL_FEATURES);

export function isValidFeature(value: string): boolean {
  return ALL_FEATURES_SET.has(value);
}

export function categoryOf(feature: string): FeatureCategory | null {
  for (const [category, features] of Object.entries(FEATURE_TAXONOMY)) {
    if ((features as readonly string[]).includes(feature)) {
      return category as FeatureCategory;
    }
  }
  return null;
}
